import {
  ChevronDown,
  ChevronUp,
  DollarSign,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import Container from "components/ClientLayoutPages/Container/Container";
import ClientNotificationHomePage from "components/Modal/Client/ClientNotificationHomePage/ClientNotificationHomePage";
import useViewTransitionNavigate from "Hooks/useViewTransitionNavigate";

import { AiFillProduct } from "react-icons/ai";
import { BsDatabaseFillAdd } from "react-icons/bs";
import { FaPhoneFlip } from "react-icons/fa6";
import { HiMiniNewspaper } from "react-icons/hi2";
import { MdPayments, MdShoppingCart } from "react-icons/md";

import { FaWallet } from "react-icons/fa";
import { useSelector } from "react-redux";
import { usePrices } from "api/Client/ads";
import React from "react";

const links = [
  {
    url: "/control-panel/categories",
    icon: <AiFillProduct className="text-blue-500" />,
    title: "سحب",
  },
  {
    url: "/control-panel/my-orders",
    icon: <MdShoppingCart className="text-red-500" />,
    title: "سجل السحب",
  },
  {
    url: "/control-panel/financial-statement",
    icon: <HiMiniNewspaper className="text-purple-500" />,
    title: "البيان المالي",
  },
  {
    url: "/control-panel/bank-notice",
    icon: <BsDatabaseFillAdd className="text-pink-500" />,
    title: "إيداع",
  },
  {
    url: "/control-panel/my-payments",
    icon: <MdPayments className="text-cyan-500" />,
    title: "سجل الإيداع",
  },

  {
    url: "/control-panel/contact-us",
    icon: <FaPhoneFlip className="text-amber-800" />,
    title: "الدعم الفني",
  },
];

export default function Home() {
  const { balance, currency } = useSelector((state) => state.balance);
  const navigate = useViewTransitionNavigate();
  const { data: rawApiData, isFetching, isSuccess, isError } = usePrices();

  return (
    <>
      <ClientNotificationHomePage />
      <Container title={" "}>
        <div className="flex flex-col gap-4">
          <div className="backdrop-blur-md w-full bg-green-500/20 border border-mainLight rounded-3xl shadow-md p-6 md:w-[320px] text-center hover:scale-105 hover:bg-mainLight/20 transition-all duration-300 cursor-default">
            <div className="flex justify-center items-center gap-4 mb-2">
              <FaWallet className="text-white text-2xl" />
              <h2 className="text-xl font-semibold text-white mb-2">
                رصيدك الحالي
              </h2>
            </div>

            <p className="text-4xl font-bold text-white" dir="ltr">
              {balance.toLocaleString()}{" "}
              <span className="text-lg text-blue-500">{currency}</span>
            </p>

            {/* <p className="text-sm text-blue-300 mt-3">
              تم آخر تحديث قبل لحظات ⏱️
            </p> */}
          </div>
          {isSuccess && (
            <>
              <h1 className="text-white mb-4 text-center text-xl  py-2 px-4 rounded-md shadow-md">
                اسعار العملات
              </h1>
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {transformData(rawApiData).map((currency) => (
                  <CurrencyCard key={currency.id} currency={currency} />
                ))}
              </div>
            </>
          )}
        </div>
      </Container>
    </>
  );
}

// =================================================================
// 2. الدوال المساعدة
// =================================================================
const safeParse = (value) => parseFloat(String(value).replace(/,/g, ""));

const transformData = (data) => {
  return data.map((item) => {
    const baseBid = safeParse(item.bid);
    const baseAsk = safeParse(item.ask);
    const originalChange = safeParse(item.change || "0");
    const originalPercentChange = safeParse(item.change_percentage || "0");

    let trend = "stable";
    if (originalChange > 0) trend = "rising";
    else if (originalChange < 0) trend = "falling";

    return {
      id: item.name,
      symbol: item.name,
      name: item.ar_name,
      bid: baseBid,
      ask: baseAsk,
      change: originalChange,
      percentChange: Math.abs(originalPercentChange),
      flag: item.icon,
      lastTrend: trend,
    };
  });
};

// =================================================================
// 3. مكون بطاقة العملة
// =================================================================
const CurrencyCard = React.memo(({ currency }) => {
  const { flag, symbol, name, bid, ask, percentChange, change } = currency;

  const isPositive = change >= 0;
  const changeColor = isPositive ? "text-green-600" : "text-red-500";
  const bgColor = isPositive ? "bg-green-100" : "bg-red-100";
  const trendIcon = isPositive ? (
    <TrendingUp size={20} className="ml-1" />
  ) : (
    <TrendingDown size={20} className="ml-1" />
  );

  const formattedBid =
    bid >= 100 ? Math.round(bid).toLocaleString("en-US") : bid.toFixed(2);
  const formattedAsk =
    ask >= 100 ? Math.round(ask).toLocaleString("en-US") : ask.toFixed(2);

  return (
    <div
      className={`bg-mainLight/30 border border-mainLight rounded-3xl p-2 shadow-xl transition-colors duration-300 ease-in-out transform hover:scale-[1.02] flex flex-col gap-1 min-w-56`}
    >
      <div className="flex items-center justify-between border-gray-200 _dark:border-slate-700 ">
        <div className="flex items-center w-full">
          {/* <img src={`/assets/images/${flag}`} alt="flag" /> */}
          <div className="mr-2 flex">
            <h3 className="font-bold text-[13px] text-gray-100 _dark:text-white">
              {name}
            </h3>
            <p className="text-[8px] text-gray-500 _dark:text-slate-400">
              {symbol}
            </p>
          </div>
        </div>

        <div
          className={`text-xs font-semibold py-1 px-2 rounded-full flex justify-center float-left items-center w-fit ${changeColor} ${bgColor}`}
        >
          {trendIcon}
          <span dir="ltr">{percentChange.toFixed(2)}%</span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-center">
        <span className="font-bold text-sm mx-auto text-gray-100 _dark:text-white p-1 rounded-full w-1/2">
          شراء
        </span>

        <span
          className={`font-bold text-sm mx-auto p-1 rounded-full w-1/2 flex text-gray-100`}
        >
          مبيع
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 text-center">
        <span
          className={`font-bold mx-auto p-1 rounded-full w-1/2 ${changeColor}`}
        >
          {formattedAsk}
        </span>

        <span
          className={`font-bold mx-auto p-1 rounded-full w-1/2 flex ${changeColor}`}
        >
          {formattedBid}
        </span>
      </div>
    </div>
  );
});
